import React, { useState, useRef, useEffect } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import { Reveal } from '../ui/revealForm';   // Sign in to another account: the email field takes the cursor (2026-10-21)

const ICON = { business: '🏢', personal: '🏠' };



export default function WorkspaceSwitcher({ mobile = false }) {
  const {
    workspaces, activeWorkspaceId, switchWorkspace, loadWorkspaces, currentUser,
    accounts, activeSlot, multiAccountEnabled, switchAccount, addAccount, removeAccount,
  } = useCurrentUser();
  const [open, setOpen]       = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [nudging, setNudging] = useState(false);
  // Multi-account add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addError, setAddError] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  const [switchingSlot, setSwitchingSlot] = useState(null);
  const ref = useRef(null);

  const hasPersonal  = workspaces.some(w => w.type === 'personal');
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const showSwitcher = workspaces.length > 1;
  const showCreate   = isSuperAdmin && !hasPersonal;
  const showAccounts = !!multiAccountEnabled;   // gated OFF until Block 195 (SW cache isolation)
  const accountsFull = (accounts?.length || 0) >= 2;

  async function handleSwitchAccount(slot) {
    if (slot === activeSlot) { setOpen(false); return; }
    setSwitchingSlot(slot);
    const r = await switchAccount(slot);
    setSwitchingSlot(null);
    if (r && r.ok === false) setAddError('That account is signed out. Remove it and sign in again.');
    else setOpen(false);
  }
  async function handleAddAccount() {
    if (addingAccount) return;
    setAddingAccount(true);
    setAddError('');
    try {
      await addAccount(addEmail.trim(), addPassword);
      setShowAddForm(false); setAddEmail(''); setAddPassword('');
      setOpen(false);
    } catch (err) {
      setAddError(err.message || 'Sign in failed');
    } finally {
      setAddingAccount(false);
    }
  }
  async function handleRemoveAccount(slot, e) {
    e.stopPropagation();
    await removeAccount(slot);
  }

  // One-time discoverability nudge (Step 3)
  useEffect(() => {
    if (workspaces.length < 2 || currentUser?.preferences?.switcherNudgeSeen) return;
    setNudging(true);
    const t = setTimeout(() => {
      setNudging(false);
      api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { switcherNudgeSeen: true } }),
      }).catch(() => {});
    }, 2200);
    return () => clearTimeout(t);
  }, [workspaces.length, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click/tap
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  if (!showSwitcher && !showCreate && !showAccounts) return null;

  const active = workspaces.find(w => w.id === activeWorkspaceId);
  const activeAccount = (accounts || []).find(a => a.slot === activeSlot);
  // With no workspace switcher, the trigger represents the active ACCOUNT instead.
  const triggerIcon = showSwitcher ? (ICON[active?.type] || '🏢') : '👤';
  const triggerLabel = showSwitcher ? (active?.name || 'Workspace') : (activeAccount?.label || currentUser?.name || 'Account');

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    setCreateError('');
    try {
      const res  = await api('/api/workspaces/personal', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.error || 'Failed to create personal workspace.'); return; }
      await loadWorkspaces();
      switchWorkspace(data.id);
      setOpen(false);
    } catch {
      setCreateError('Failed to create personal workspace. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  // UI-4: the workspace tile of brief 4 (an icon tile, a WORKSPACE label over the name, ⇅).
  // Tokens only; the layout is ready for several businesses listed by name (TEAM-1a).
  const triggerStyle = {
    display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
    width: '100%', boxSizing: 'border-box', textAlign: 'left',
    padding: 'var(--space-2) var(--space-2-5)', minHeight: 44,
    background: 'var(--bg)', border: 'var(--border) solid var(--chip-line)', borderRadius: 'var(--radius-button)',
    cursor: 'pointer', fontFamily: 'var(--font-body)', color: 'var(--ink)',
  };

  const dropdownStyle = {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: 'var(--surface)', border: 'var(--border) solid var(--line)', borderRadius: 'var(--radius-popover)',
    boxShadow: 'var(--shadow-popover)', zIndex: 'var(--z-popover)',
    overflow: 'hidden', marginTop: 'var(--space-1)', padding: 'var(--space-1-5)',
  };

  const itemStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
    width: '100%', boxSizing: 'border-box', minHeight: 44,
    padding: 'var(--space-2) var(--space-2-5)', border: 'none', borderRadius: 'var(--radius-button-sm)', textAlign: 'left',
    fontSize: 'var(--shell-text)', fontFamily: 'var(--font-body)', cursor: 'pointer',
    background: isActive ? 'var(--surface-2)' : 'var(--surface)',
    fontWeight: isActive ? 700 : 600, color: 'var(--ink)',
  });
  const tile = (type) => ({
    flex: '0 0 auto', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 'var(--radius-checkbox)', border: 'var(--border) solid var(--line-soft)', fontSize: 'var(--text-secondary)',
    background: type === 'personal' ? 'var(--pastel-pink)' : type === 'account' ? 'var(--pastel-neutral)' : 'var(--pastel-blue)',
  });

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: mobile ? 0 : '0.5rem' }}>

      {(showSwitcher || showAccounts) ? (
        <button
          onClick={() => setOpen(o => !o)}
          title={showSwitcher ? 'Switch workspace' : 'Switch account'}
          style={triggerStyle}
          className={`ws-trigger${nudging ? ' ws-nudge' : ''}`}
        >
          <span style={tile(showSwitcher ? active?.type : 'account')} aria-hidden="true">{triggerIcon}</span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontSize: 'var(--shell-text-small)', fontWeight: 700, letterSpacing: 'var(--tracking-eyebrow)', textTransform: 'uppercase', color: 'var(--ink-2)' }}>
              {showSwitcher ? 'Workspace' : 'Account'}
            </span>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--shell-text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{triggerLabel}</span>
          </span>
          <span aria-hidden="true" style={{ fontSize: 'var(--text-badge)', color: 'var(--ink-3)' }}>⇅</span>
        </button>
      ) : (
        <button
          onClick={handleCreate}
          disabled={creating}
          title="Add personal workspace"
          style={{ ...triggerStyle, color: 'var(--ink)' }}
          className="ws-trigger"
        >
          <span style={{ fontSize: 'var(--text-small)' }}>➕</span>
          <span style={{ flex: 1 }}>{creating ? 'Creating...' : 'Add personal workspace'}</span>
        </button>
      )}

      {open && (
        <div style={dropdownStyle}>
          {workspaces.map(w => {
            const isActive = w.id === activeWorkspaceId;
            return (
              <button
                key={w.id}
                onClick={() => { switchWorkspace(w.id); setOpen(false); }}
                style={itemStyle(isActive)}
              >
                <span style={tile(w.type)} aria-hidden="true">{ICON[w.type] || '🏢'}</span>
                <span style={{ flex: 1 }}>{w.name}</span>
                {isActive && <span style={{ color: 'var(--ink)', fontSize: 'var(--text-small)' }}>✓</span>}
              </button>
            );
          })}
          {showCreate && (
            <>
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{ ...itemStyle(false), borderTop: 'var(--border) solid var(--line-soft)', color: 'var(--ink)' }}
              >
                <span>➕</span>
                <span style={{ flex: 1 }}>{creating ? 'Creating...' : 'Add personal workspace'}</span>
              </button>
              {createError && (
                <p style={{ margin: 0, padding: '0.3rem 0.9rem', fontSize: 'var(--text-small)', color: 'var(--danger)' }}>
                  {createError}
                </p>
              )}
            </>
          )}

          {showAccounts && (
            <>
              <div style={{ borderTop: 'var(--border) solid var(--line-soft)', padding: '0.5rem 0.9rem 0.2rem', fontSize: 'var(--text-eyebrow)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-3)', fontFamily: 'var(--font-body)' }}>
                Accounts
              </div>
              {(accounts || []).map(a => {
                const isActive = a.slot === activeSlot;
                return (
                  <div key={a.slot} style={{ ...itemStyle(isActive), justifyContent: 'space-between' }}>
                    <button
                      onClick={() => handleSwitchAccount(a.slot)}
                      disabled={switchingSlot === a.slot}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}
                    >
                      <span>👤</span>
                      <span style={{ flex: 1 }}>{switchingSlot === a.slot ? 'Switching…' : a.label}</span>
                      {isActive && <span style={{ color: 'var(--ink)', fontSize: 'var(--text-small)' }}>✓</span>}
                    </button>
                    {!isActive && (
                      <button
                        title="Remove account from this browser"
                        onClick={(e) => handleRemoveAccount(a.slot, e)}
                        style={{ background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 'var(--text-body)', lineHeight: 1, padding: '0 0.25rem' }}
                      >×</button>
                    )}
                  </div>
                );
              })}
              {!accountsFull && !showAddForm && (
                <button
                  onClick={() => { setShowAddForm(true); setAddError(''); }}
                  style={{ ...itemStyle(false), color: 'var(--ink)' }}
                >
                  <span>➕</span>
                  <span style={{ flex: 1 }}>Sign in to another account</span>
                </button>
              )}
              {showAddForm && (
                <Reveal style={{ padding: '0.5rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <input type="email" placeholder="Email" value={addEmail} autoComplete="off"
                    onChange={e => setAddEmail(e.target.value)}
                    style={{ padding: '0.4rem', border: 'var(--border) solid var(--line)', borderRadius: 6, fontSize: 'var(--text-input)', fontFamily: 'var(--font-body)' }} />
                  <input type="password" placeholder="Password" value={addPassword} autoComplete="off"
                    onChange={e => setAddPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddAccount(); }}
                    style={{ padding: '0.4rem', border: 'var(--border) solid var(--line)', borderRadius: 6, fontSize: 'var(--text-input)', fontFamily: 'var(--font-body)' }} />
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button onClick={handleAddAccount} disabled={addingAccount || !addEmail || !addPassword}
                      style={{ flex: 1, padding: '0.4rem', border: 'none', borderRadius: 6, background: 'var(--highlight)', color: 'var(--ink)', cursor: 'pointer', fontSize: 'var(--text-small)', fontFamily: 'var(--font-body)', fontWeight: 600 }}>
                      {addingAccount ? 'Signing in…' : 'Sign in'}
                    </button>
                    <button onClick={() => { setShowAddForm(false); setAddError(''); }}
                      style={{ padding: '0.4rem 0.6rem', border: 'var(--border) solid var(--line)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', fontSize: 'var(--text-small)', fontFamily: 'var(--font-body)' }}>
                      Cancel
                    </button>
                  </div>
                </Reveal>
              )}
              {addError && (
                <p style={{ margin: 0, padding: '0.3rem 0.9rem 0.5rem', fontSize: 'var(--text-small)', color: 'var(--danger)' }}>{addError}</p>
              )}
            </>
          )}
        </div>
      )}

      {createError && !open && (
        <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-small)', color: 'var(--danger)' }}>{createError}</p>
      )}
    </div>
  );
}
